import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ArrowLeft, Shield, Server, Database, Cookie } from "lucide-react";
import useSEO from "@/hooks/useSEO";

export default function Legal() {
  const { t } = useTranslation();

  useSEO({
    title: t("legal.seo_title"),
    description: t("legal.seo_description"),
    canonical: "https://iam-calculator.emergent.host/mentions-legales",
  });

  const providerLines = t("legal.hosting.provider_lines", { returnObjects: true });

  return (
    <div className="min-h-screen bg-slate-50">
      <Header minimal />
      <div className="max-w-4xl mx-auto px-6 lg:px-12 pt-10 pb-24">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition mb-8" data-testid="legal-back">
          <ArrowLeft className="w-4 h-4" /> {t("legal.back_home")}
        </Link>

        <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-3">{t("legal.title")}</div>
        <h1 className="text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 leading-tight mb-10">
          {t("legal.title")}
        </h1>

        <div className="space-y-8">
          <Section title={t("legal.editor.title")}>
            <p className="text-slate-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: t("legal.editor.p1") }} />
            <p className="text-slate-900 font-medium mt-3">
              128 rue La Boétie<br />
              75008 Paris<br />
              France
            </p>
            <div className="mt-4 text-sm text-slate-600" dangerouslySetInnerHTML={{ __html: t("legal.editor.publication") }} />
          </Section>

          <Section title={t("legal.hosting.title")} icon={Server}>
            <p className="text-slate-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: t("legal.hosting.p1") }} />
            <div className="mt-4 rounded-2xl bg-slate-50 border border-slate-200 p-5 text-sm text-slate-700 leading-relaxed">
              <div className="font-semibold text-slate-900 mb-2">{t("legal.hosting.provider_title")}</div>
              {providerLines.map((line, i) => (
                <div key={i} className={i >= 2 && i <= 4 ? "mt-1" : ""} dangerouslySetInnerHTML={{ __html: line }} />
              ))}
              <div className="mt-2">
                Site : <a href="https://www.ovhcloud.com" target="_blank" rel="noopener noreferrer" className="text-fuchsia-600 hover:underline">www.ovhcloud.com</a>
              </div>
            </div>
            <p className="text-slate-700 leading-relaxed mt-4" dangerouslySetInnerHTML={{ __html: t("legal.hosting.p2") }} />
            <p className="text-slate-700 leading-relaxed mt-3" dangerouslySetInnerHTML={{ __html: t("legal.hosting.p3") }} />
          </Section>

          <Section title={t("legal.data.title")} icon={Database}>
            <p className="text-slate-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: t("legal.data.p1") }} />
            <p className="text-slate-700 leading-relaxed mt-3" dangerouslySetInnerHTML={{ __html: t("legal.data.p2") }} />
            <div className="mt-5 p-5 rounded-2xl bg-fuchsia-50 border border-fuchsia-100">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-fuchsia-600 shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: t("legal.data.rights") }} />
              </div>
            </div>
          </Section>

          <Section title={t("legal.cookies.title")} icon={Cookie}>
            <p className="text-slate-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: t("legal.cookies.text") }} />
          </Section>

          <Section title={t("legal.ip.title")}>
            <p className="text-slate-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: t("legal.ip.text") }} />
          </Section>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-8">
      <div className="flex items-center gap-3 mb-4">
        {Icon && (
          <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center">
            <Icon className="w-4 h-4 text-white" />
          </div>
        )}
        <h2 className="text-xl font-bold text-slate-900 tracking-tight">{title}</h2>
      </div>
      <div className="text-sm">{children}</div>
    </section>
  );
}
